import { Component, OnInit } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  take,
  fromEvent,
  filter,
  map,
  distinctUntilChanged,
  switchMap,
  tap,
  combineLatest,
} from 'rxjs';

import { IMessageRoom } from '@shared/models/messages';
import { SocketService } from '@core/services/socket.service';
import { MessagesService } from '@core/services/messages.service';
import { MagicTokenService } from '@core/services/magic-token.service';
import { DEFAULT_FEATURE_FLAGS } from '@core/constants/feature-flags';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import {
  selectConfigOpenChatWidgetId,
  selectConfigOpenModal,
} from '@state/config/config.selectors';
import { selectAuthToken, selectAuthUser } from '@state/auth/auth.selectors';
import {
  setReferrerCodeAction,
  setEntityIdAction,
  setAuthModal,
} from '@state/auth/auth.actions';
import {
  fetchFeatureFlagsAction,
  setOpenChatWidgetIdAction,
} from '@state/config/config.actions';
import {
  setOrganizationUrlSlugAction,
  getOrganizationByShortNameAction,
  setLastOrganizationUrlSlugAction,
} from '@state/organization/organization.actions';
import { selectOrganizationUrlSlug } from '@state/organization/organization.selectors';
import { listMessagesByRoomAction } from '@state/messages/messages.actions';

import { environment } from '../environments/environment';

const findRouteParam = (
  route: ActivatedRoute,
  param: string
): string | null => {
  while (route) {
    if (route.snapshot.paramMap.has(param)) {
      return route.snapshot.paramMap.get(param);
    }
    route = route.firstChild!;
  }
  return null;
};

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  protected title = 'marketplace-frontend';
  public maintenance = false;

  protected authToken: string | undefined;
  protected urlOrgSlug: string | null = null;
  protected userOrgShortName: string | undefined | null;

  protected isDetailsScreen = false;
  protected isHomeScreen = false;
  protected isLandingPage = false;

  protected lastOrgSlugLoaded: string | null | undefined;
  protected newMessages: IMessageRoom[] = [];
  protected currentOpenChatWidgetId: string | undefined;

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private route: ActivatedRoute,
    private messagesService: MessagesService,
    private socketService: SocketService,
    private magicTokenService: MagicTokenService
  ) {
    if (!environment.production) {
      console.log('Environment: ', environment.name);
    }
  }

  ngOnInit(): void {
    // Handle redirects to m.rebuilt.com if enabled
    this.handleMarketplaceRedirects();

    window.addEventListener('message', this.handlePostMessage.bind(this));

    const currentPath = window.location.pathname;
    const isEmbedRoute = currentPath.startsWith('/embed');

    if (isEmbedRoute) {
      console.log('✅ Embed screen detected - limited app initialization');
      document.body.classList.add('embed-route');
    } else {
      document.body.classList.remove('embed-route');
      this.handleRefCode();
      this.handleEntityId();
    }

    if (this.maintenance) {
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationStart && event.url !== '/maintenance') {
          this.router.navigate(['/maintenance']);
        }
      });
      return;
    } else {
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationStart && event.url === '/maintenance') {
          this.router.navigate(['/']);
        }
      });
    }
    this.fetchFeatureFlags();

    /**
     * Subscribe to the auth token in the store and cache it.
     * This cached value is used to compare against cross-tab updates
     * received via the localStorage `storage` listener below.
     */
    this.store
      .select(selectAuthToken)
      .pipe(untilDestroyed(this))
      .subscribe((token) => {
        this.authToken = token;
      });

    // Listen for cross-tab auth changes via localStorage `storage` event
    fromEvent<StorageEvent>(window, 'storage')
      .pipe(
        filter((event) => event.key === 'auth'),
        map((event) => this.parseAuth(event.newValue)),
        map((auth) => auth?.token ?? auth?.user?.token ?? null),
        distinctUntilChanged(),
        untilDestroyed(this)
      )
      .subscribe((nextToken) => {
        // If the token changed (login/logout in another tab), reload to reset app state
        const current = this.authToken ?? null;
        if (nextToken !== current) {
          window.location.reload();
        }
      });

    this.store
      .select(selectConfigOpenModal)
      .pipe(untilDestroyed(this))
      .subscribe((openModal) => {
        if (!!openModal) {
          document.body.classList.add('no-scroll');
        } else {
          document.body.classList.remove('no-scroll');
        }
      });

    this.store
      .select(selectAuthUser)
      .pipe(untilDestroyed(this))
      .subscribe((user) => {
        this.userOrgShortName = user?.organization?.shortName;
      });

    /**
     * Organization related - skip for embed routes
     */
    if (!isEmbedRoute) {
      this.router.events.pipe(untilDestroyed(this)).subscribe((event) => {
        this.isDetailsScreen =
          !!this.router.url.startsWith('/properties/') ||
          (!!this.router.url.includes('/o/') &&
            !!this.router.url.includes('/properties/'));
        this.isHomeScreen =
          ((this.router.url === '/' ||
            this.router.url.startsWith('/page/') ||
            this.router.url.startsWith('/?')) &&
            !this.router.url.startsWith('/maintenance') &&
            !this.router.url.startsWith('/disclosures')) ||
          this.router.url.startsWith('/o/');
        this.isLandingPage = this.router.url.includes('/lp');

        if (event instanceof NavigationEnd) {
          const newSlug = findRouteParam(this.route.root, 'slug');
          // Only update it if user is on the home
          // or property details screen
          if (this.isHomeScreen || this.isDetailsScreen || this.isLandingPage) {
            if (newSlug !== this.urlOrgSlug) {
              this.urlOrgSlug = newSlug;
              this.onSlugChanged(newSlug);
            }
          }
        }
      });

      // Load organization on slug change
      this.store
        .select(selectOrganizationUrlSlug)
        .pipe(untilDestroyed(this))
        .subscribe((slug) => {
          if (slug && slug !== this.lastOrgSlugLoaded) {
            const params = new URLSearchParams(window.location.search);
            // Support both short-form (r, c, s) and long-form (refCode, campaignName, source) query params
            const refCode =
              params.get('r') || params.get('refCode') || undefined;
            const campaignName =
              params.get('c') || params.get('campaignName') || undefined;
            const source = params.get('s') || params.get('source') || undefined;

            this.store.dispatch(
              getOrganizationByShortNameAction({
                shortName: slug,
                campaignName: campaignName || source || null,
                refCode: refCode || null,
              })
            );
          }
          this.lastOrgSlugLoaded = slug;
        });

      /**
       * Messages related
       */
      const ready$ = this.socketService.ready$.pipe(filter((ready) => ready));
      const isLoggedIn$ = this.store.select(selectAuthToken).pipe(
        map((token) => !!token),
        filter((loggedIn): loggedIn is true => loggedIn)
      );

      this.store
        .select(selectConfigOpenChatWidgetId)
        .pipe(untilDestroyed(this))
        .subscribe((openChatWidgetId) => {
          this.currentOpenChatWidgetId = openChatWidgetId;
        });

      combineLatest([ready$, isLoggedIn$])
        .pipe(
          take(1),
          tap(() => {
            console.log('Socket ready, subscribing to room message created...');
          }),
          switchMap(() => this.messagesService.subscribeToRoomMessageCreated()),
          untilDestroyed(this)
        )
        .subscribe({
          next: (message) => {
            // Only add to new messages notifications if from a different roomId
            const messageRoomId =
              message.created.propertyRoom?.id ||
              message.created.generalRoom.id;
            if (messageRoomId !== this.currentOpenChatWidgetId) {
              this.newMessages.push(message.created);
            }
          },
          error: (error) => {
            console.error(
              'socket: Error subscribing to room message created:',
              error
            );
          },
        });
    }
  }

  private onSlugChanged(slug: string | null) {
    this.store.dispatch(
      setOrganizationUrlSlugAction({
        slug: slug,
      })
    );

    /**
     * Is slug is not rebuilt, is not null and is not the same
     * as the current user, update the last slug
     */
    if (
      !!slug &&
      slug.toLowerCase() !== 'rebuilt' &&
      slug !== this.userOrgShortName
    ) {
      this.store.dispatch(
        setLastOrganizationUrlSlugAction({ slug: slug.toLowerCase() })
      );
    }
  }

  private parseAuth(
    value: string | null
  ): { token?: string; user?: { token?: string } } | null {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  fetchFeatureFlags(): void {
    this.store.dispatch(
      fetchFeatureFlagsAction({ keys: [...DEFAULT_FEATURE_FLAGS] })
    );
  }

  getUrl(): string {
    return window.location.href;
  }

  handleRefCode() {
    const url = this.getUrl();
    const params = new URLSearchParams(url.split('?')[1]);
    const referrerCode = params.get('refCode');
    if (referrerCode) {
      this.store
        .select(selectAuthToken)
        .pipe(take(1))
        .subscribe((token) => {
          if (!token) {
            this.store.dispatch(setReferrerCodeAction({ referrerCode }));
          }
        });
      this.store.dispatch(setAuthModal({ authModal: 'signup' }));
    }
  }

  handleEntityId() {
    const url = this.getUrl();
    const params = new URLSearchParams(url.split('?')[1]);

    const entityId = params.get('entityId');
    if (entityId) {
      this.store
        .select(selectAuthToken)
        .pipe(take(1))
        .subscribe((token) => {
          if (!token) {
            this.store.dispatch(setEntityIdAction({ entityId }));
          }
        });
    }
  }

  openMessageRoom(messageRoom: IMessageRoom): void {
    (window as any).ChatWidget?.destroy();
    this.store.dispatch(setOpenChatWidgetIdAction({}));
    this.removeMessage(messageRoom);

    setTimeout(() => {
      const type = messageRoom.propertyRoom?.propertyId
        ? 'property'
        : 'general';
      const roomId =
        type === 'general'
          ? messageRoom.generalRoom.id
          : messageRoom.propertyRoom?.id;

      this.magicTokenService
        .requestMagicToken()
        .pipe(take(1))
        .subscribe((token) => {
          (window as any).ChatWidget?.openRoom(roomId, type, {
            magicToken: token.requestMagicToken,
          });

          this.store.dispatch(
            setOpenChatWidgetIdAction({ openChatWidgetId: roomId })
          );

          this.store.dispatch(
            listMessagesByRoomAction({
              sort: [{ field: 'UPDATED_AT', order: 'DESC' }],
            })
          );
        });
    }, 0);
  }

  /**
   * Remove all messages for the same general room
   */
  removeMessage(messageRoom: IMessageRoom) {
    this.newMessages = this.newMessages.filter((message) => {
      return messageRoom.propertyRoom
        ? message.propertyRoom?.id !== messageRoom.propertyRoom.id
        : message.generalRoom.id !== messageRoom.generalRoom.id;
    });

    this.store.dispatch(
      listMessagesByRoomAction({
        sort: [{ field: 'UPDATED_AT', order: 'DESC' }],
      })
    );
  }

  // window.parent.postMessage({ type: 'openChatWidget', roomId: 'abc123' }, '*');
  // window.parent.postMessage({ type: 'destroyChatWidget' }, '*');
  handlePostMessage(event: MessageEvent) {
    if (event.data?.type === 'destroyChatWidget') {
      this.store.dispatch(setOpenChatWidgetIdAction({}));
    }

    if (event.data?.type === 'openChatWidget') {
      this.store.dispatch(
        setOpenChatWidgetIdAction({ openChatWidgetId: event.data.roomId })
      );
    }
  }

  /**
   * Redirects to m.rebuilt.com if the feature is enabled in environment config
   * Handles two cases:
   * 1. Root path (/) -> redirects to m.rebuilt.com
   * 2. /properties/<id> -> redirects to m.rebuilt.com/properties/<id>
   */
  private handleMarketplaceRedirects(): void {
    if (!environment.redirectToMRebuilt) {
      return;
    }

    const hostname = window.location.hostname;
    const pathname = window.location.pathname;
    const search = window.location.search;

    // Only redirect if we're on marketplace.rebuilt.com
    if (hostname === 'frontend-develop.d1ek9lvhu5u19c.amplifyapp.com') {
      // Check if it's the root path or /properties/<id>
      const propertiesMatch = pathname.match(/^\/properties\/([^\/]+)$/);

      if (pathname === '/' || propertiesMatch) {
        const targetUrl = `https://m.rebuilt.com${pathname}${search}`;
        window.location.replace(targetUrl);
      }
    }
  }
}
